import { SetupMutationFn } from 'core/configure/types';
import { BaseHttpError } from 'core/services/api/errors';
import { BatchMembershipInvite, Community, MembershipInvite } from 'core/types';
import { patchDeclinedInvitationKey } from 'core/utils/mutation-key-factory';
import {
	findRecordFromInfinityQueryCache,
	onPostMutateOptimisticInfinityQueryCache,
	PaginatedRecordsSnapshot,
	removeRecordFromInfinityQueryCache,
} from 'core/utils/optimistic-utils';
import { communityKeys } from 'core/utils/query-key-factory';
import { isCancelledError, useMutation } from '@tanstack/react-query';

export interface PatchDeclinedInvitesInput {
	communityId: string;
	invitationIds: string[];
	action: 'delete' | 'reinvite';
}

export const usePatchDeclinedInvites = () => {
	const mutation = useMutation<
		MembershipInvite,
		BaseHttpError,
		PatchDeclinedInvitesInput
	>({
		mutationKey: [patchDeclinedInvitationKey],
	});

	return mutation;
};

type SnapshotArray = {
	listSnapshot: PaginatedRecordsSnapshot<MembershipInvite>;
}[];

export const setupPatchDeclinedInvites: SetupMutationFn = (
	services,
	createTrackedParallelMutation,
	mutationTracker
) => {
	const { api, queryClient } = services;

	const mutation = createTrackedParallelMutation<
		BatchMembershipInvite,
		BaseHttpError,
		PatchDeclinedInvitesInput,
		null
	>({
		mutationFn: ({ communityId, invitationIds, action }) =>
			api
				.patchCommunityMembershipInvitations(
					communityId,
					invitationIds,
					action
				)
				.then(response => response.data),
		onMutate: async ({ communityId, invitationIds, action }) => {
			const currentInviteInfos: SnapshotArray = [];

			for (const inviteId of invitationIds) {
				const currentInviteInfo =
					findRecordFromInfinityQueryCache<MembershipInvite>(
						queryClient,
						communityKeys.declinedInvites(communityId),
						inviteId
					);

				await removeRecordFromInfinityQueryCache<MembershipInvite>(
					queryClient,
					communityKeys.declinedInvites(communityId),
					inviteId
				);

				if (action === 'delete') {
					queryClient.setQueryData<Community | undefined>(
						communityKeys.detail(communityId),
						community => {
							if (community) {
								community.team_stats.declined--;
							}

							return community;
						}
					);
				} else {
					const updatedRecord = {
						...currentInviteInfo?.record,
						status: 'sent',
						created_dt: new Date().toISOString(),
					} as MembershipInvite;

					await onPostMutateOptimisticInfinityQueryCache<MembershipInvite>(
						queryClient,
						communityKeys.pendingInvites(communityId),
						null,
						updatedRecord,
						[],
						false
					);

					queryClient.setQueryData<Community | undefined>(
						communityKeys.detail(communityId),
						community => {
							if (community) {
								community.team_stats.declined--;
								community.team_stats.invites++;
							}

							return community;
						}
					);
				}

				if (currentInviteInfo) {
					currentInviteInfos.push(currentInviteInfo);
				}
			}

			return null;
		},
		retry: 1,
	});

	queryClient.setMutationDefaults([patchDeclinedInvitationKey], mutation);
};
